import { Icon } from "@equinor/e-procurement-portal-and-ts-portal-front-end-components";
import { mdiArrowLeft } from "@mdi/js";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import { createFileRoute, redirect } from "@tanstack/react-router";
import dayjs from "dayjs";
import { enqueueSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import * as DeliveryTypes from "../../../../@types/services/DeliverySchedule";
import { InfoRow } from "../../../../components/atoms/InfoRow/InfoRow";
import { FileList } from "../../../../components/molecules/FileList";
import { PackagesSection } from "../../../../components/molecules/PackagesSection/PackagesSection";
import { useAuth } from "../../../../hooks/useAuth";
import {
  approveDeliverySchedule,
  cancelDeliverySchedule,
  cancelDeliveryScheduleByVendor,
  getDeliverySchedule,
} from "../../../../services/deliverySchedule";
import { i18nextParseToDate } from "../../../../utils/i18nextParseTo";

// TODO: Replace the hardcoded colors in the code with theme variables.
const SectionContainer: React.FC<{
  children: React.ReactNode;
  hasBackground?: boolean;
}> = ({ children, hasBackground = true }) => (
  <Box
    sx={{
      backgroundColor: hasBackground ? "#f8f9fa" : "transparent",
      borderRadius: hasBackground ? 1 : 0,
      mb: 4,
      p: 2,
    }}
  >
    {children}
  </Box>
);

const SectionTitle: React.FC<{
  icon: string;
  title: string;
  statusChip?: React.ReactNode;
}> = ({ icon, title, statusChip }) => (
  <Box
    sx={{
      alignItems: "center",
      display: "flex",
      gap: 2,
      mb: 3,
    }}
  >
    <Typography sx={{ fontWeight: "bold" }} variant="h6">
      {icon} {title}
    </Typography>
    {statusChip}
  </Box>
);

const DeliverySchedulingShow: React.FC = () => {
  const { user } = useAuth();

  const navigate = Route.useNavigate();

  const { deliveryScheduleId } = Route.useParams();
  const { t } = useTranslation();

  const [isLoading, setIsLoading] = useState(true);
  const [deliveryDetails, setDeliveryDetails] =
    useState<DeliveryTypes.DeliveryScheduling | null>(null);
  const [isActionLoading, setIsActionLoading] = useState<string | null>(null);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [cancelJustification, setCancelJustification] = useState("");

  useEffect(() => {
    const fetchDeliveryDetails = async (): Promise<void> => {
      if (!deliveryScheduleId) {
        return;
      }

      try {
        const data = await getDeliverySchedule(deliveryScheduleId);
        setDeliveryDetails(data);
      } catch (error) {
        console.error(error);
        enqueueSnackbar(t("deliveryScheduling.fetchDetailsError"), {
          variant: "error",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchDeliveryDetails();
  }, [deliveryScheduleId, t]);

  const handleBack = useCallback((): void => {
    navigate({ to: "/delivery_schedules" });
  }, [navigate]);

  const isPastDate = useCallback((date: Date | string): boolean => {
    const today = dayjs().startOf("day");
    return dayjs(date).isBefore(today);
  }, []);

  const handleApprove = async (): Promise<void> => {
    if (!deliveryScheduleId || !deliveryDetails) return;

    try {
      setIsActionLoading("approve");
      await approveDeliverySchedule(deliveryScheduleId);
      enqueueSnackbar(t("deliveryScheduling.approveSuccess"), {
        variant: "success",
      });
      navigate({ to: "/delivery_schedules" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(t("deliveryScheduling.approveError"), {
        variant: "error",
      });
    } finally {
      setIsActionLoading(null);
    }
  };

  const handleCancel = async (): Promise<void> => {
    if (!deliveryScheduleId || !deliveryDetails) return;

    if (user?.roles_names.includes("Delivery Schedule - Manage Time Slot")) {
      setIsCancelModalOpen(true);
    } else if (user?.roles_names.includes("Delivery Schedule - Cancel")) {
      try {
        setIsActionLoading("cancel");
        await cancelDeliveryScheduleByVendor(deliveryScheduleId);
        enqueueSnackbar(t("deliveryScheduling.cancelSuccess"), {
          variant: "success",
        });
        navigate({ to: "/delivery_schedules" });
      } catch (error) {
        console.error(error);
        enqueueSnackbar(t("deliveryScheduling.cancelError"), {
          variant: "error",
        });
      } finally {
        setIsActionLoading(null);
      }
    }
  };

  const handleCancelConfirm = async (): Promise<void> => {
    if (!deliveryScheduleId || !deliveryDetails) return;
    setIsActionLoading("cancel");
    try {
      await cancelDeliverySchedule(deliveryScheduleId, cancelJustification);
      enqueueSnackbar(t("deliveryScheduling.cancelSuccess"), {
        variant: "success",
      });
      setIsCancelModalOpen(false);
      setCancelJustification("");
      navigate({ to: "/delivery_schedules" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar(t("deliveryScheduling.cancelError"), {
        variant: "error",
      });
    } finally {
      setIsActionLoading(null);
    }
  };

  const handleCancelModalClose = (): void => {
    setIsCancelModalOpen(false);
    setCancelJustification("");
  };

  if (isLoading || !deliveryDetails) {
    return (
      <Box sx={{ boxSizing: "border-box", p: 3, width: "100%" }}>
        <Typography>{t("loading")}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ boxSizing: "border-box", p: 3, width: "100%" }}>
      <Box
        sx={{
          alignItems: "center",
          display: "flex",
          gap: 2,
          mb: 3,
          width: "100%",
        }}
      >
        <IconButton onClick={handleBack} size="small">
          <Icon icon={mdiArrowLeft} />
        </IconButton>
        <Typography variant="h5">
          {t("deliveryScheduling.deliveryDetails")}
        </Typography>
      </Box>
      {/* TODO: Replace the hardcoded icon and colors */}
      <Paper sx={{ p: 3, width: "100%" }}>
        <SectionTitle
          icon="📅"
          statusChip={
            <Chip
              label={t(
                DeliveryTypes.DeliveryStatusLabels[deliveryDetails.status],
              )}
              size="small"
              sx={{
                "& .MuiChip-label": {
                  color: "#ffffff",
                },
                backgroundColor:
                  DeliveryTypes.statusColors[deliveryDetails.status]?.color,
                color: "#ffffff",
                fontWeight: "bold",
              }}
            />
          }
          title={t("deliveryScheduling.deliveryInfo")}
        />
        <SectionContainer>
          <Box
            sx={{
              display: "grid",
              gap: 3,
              gridTemplateColumns: {
                lg: "repeat(5, 1fr)",
                md: "repeat(3, 1fr)",
                sm: "repeat(2, 1fr)",
                xs: "1fr",
              },
            }}
          >
            {deliveryDetails.purchase_order ? (
              <InfoRow
                label={t("materialRequestForm.purchaseOrder")}
                value={deliveryDetails.purchase_order}
              />
            ) : (
              <InfoRow
                label={t("deliveryScheduling.documentReference")}
                value={
                  deliveryDetails.document_reference ||
                  t("deliveryScheduling.notInformed")
                }
              />
            )}
            <InfoRow
              label={t("deliveryScheduling.deliveryDateTime")}
              value={t(
                "intlDateTime",
                i18nextParseToDate(deliveryDetails.delivery_date_time),
              )}
            />
            <InfoRow
              label={t("quotation.vendor")}
              value={
                deliveryDetails.vendor || t("deliveryScheduling.notInformed")
              }
            />
            <InfoRow
              label={t("common.taxNumber")}
              value={
                deliveryDetails.cnpj || t("deliveryScheduling.notInformed")
              }
            />
            <InfoRow
              label={t("deliveryScheduling.plate")}
              value={
                deliveryDetails.license_plate ||
                t("deliveryScheduling.notInformed")
              }
            />
          </Box>
        </SectionContainer>

        {/* TODO: Replace the hardcoded icon */}
        <SectionTitle
          icon="📄"
          title={t("deliveryScheduling.form.invoiceInformation")}
        />
        <SectionContainer>
          <Box
            sx={{
              display: "grid",
              gap: 3,
              gridTemplateColumns: {
                lg: "1fr 1fr 3fr",
                md: "repeat(3, 1fr)",
                sm: "repeat(2, 1fr)",
                xs: "1fr",
              },
            }}
          >
            <InfoRow
              label={t("deliveryScheduling.invoice")}
              value={String(deliveryDetails.invoice_number)}
            />
            <InfoRow
              label={t("deliveryScheduling.invoiceSeries")}
              value={deliveryDetails.invoice_series}
            />
            {!!deliveryDetails.invoice_key && (
              <InfoRow
                label={t("deliveryScheduling.invoiceKey")}
                value={deliveryDetails.invoice_key}
              />
            )}
          </Box>
        </SectionContainer>

        {deliveryDetails.file_attachments &&
          deliveryDetails.file_attachments.length > 0 && (
            <>
              {/* TODO: Replace the hardcoded icon */}
              <SectionTitle icon="🔗" title={t("deliveryScheduling.files")} />
              <SectionContainer>
                <FileList attachments={deliveryDetails.file_attachments} />
              </SectionContainer>
            </>
          )}

        {!!deliveryDetails.observations && (
          <>
            {/* TODO: Replace the hardcoded icon */}
            <SectionTitle
              icon="⚠️"
              title={t("deliveryScheduling.cancellationReason")}
            />
            <SectionContainer>
              <Typography
                sx={{
                  color: "text.primary",
                }}
              >
                {deliveryDetails.observations}
              </Typography>
            </SectionContainer>
          </>
        )}

        {!!deliveryDetails.packages && (
          <>
            <SectionTitle
              icon="📦"
              title={t("deliveryScheduling.packages.title")}
            />
            <PackagesSection packages={deliveryDetails.packages} />
          </>
        )}

        <Divider sx={{ my: 3 }} />
        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end" }}>
          {(user?.roles_names.includes("Delivery Schedule - Cancel") ||
            user?.roles_names.includes(
              "Delivery Schedule - Manage Time Slot",
            )) &&
            [
              DeliveryTypes.DeliveryStatus.PENDING,
              DeliveryTypes.DeliveryStatus.APPROVED,
            ].includes(deliveryDetails.status) &&
            !isPastDate(deliveryDetails.delivery_date_time) && (
              <Button
                color="error"
                disabled={!!isActionLoading}
                onClick={handleCancel}
                startIcon={
                  isActionLoading === "cancel" ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null
                }
                variant="contained"
              >
                {t("cancel")}
              </Button>
            )}
          {user?.roles_names.includes("Delivery Schedule - Approve") &&
            deliveryDetails.status === DeliveryTypes.DeliveryStatus.PENDING && (
              <Button
                color="success"
                disabled={!!isActionLoading}
                onClick={handleApprove}
                startIcon={
                  isActionLoading === "approve" ? (
                    <CircularProgress color="inherit" size={20} />
                  ) : null
                }
                variant="contained"
              >
                {t("calendar.approve")}
              </Button>
            )}
        </Box>
      </Paper>
      <Dialog
        fullWidth
        maxWidth="sm"
        onClose={handleCancelModalClose}
        open={isCancelModalOpen}
      >
        <DialogTitle>{t("calendar.cancelSchedule")}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            helperText={`${cancelJustification.length}/1000 caracteres`}
            label={t("calendar.cancelJustification")}
            margin="dense"
            multiline
            onChange={(e) => setCancelJustification(e.target.value)}
            rows={4}
            slotProps={{
              input: {
                inputProps: {
                  maxLength: 1000,
                },
              },
            }}
            sx={{ mt: 2 }}
            value={cancelJustification}
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelModalClose}>{t("common.cancel")}</Button>
          <Button
            color="error"
            disabled={
              !cancelJustification.trim() || isActionLoading === "cancel"
            }
            onClick={handleCancelConfirm}
            startIcon={
              isActionLoading === "cancel" ? (
                <CircularProgress color="inherit" size={20} />
              ) : null
            }
            variant="contained"
          >
            {t("calendar.confirmCancellation")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export const Route = createFileRoute(
  "/_private/delivery_schedules/$deliveryScheduleId/",
)({
  beforeLoad: ({ context }) => {
    const { auth } = context;
    if (!auth.user?.roles_names.includes("Delivery Schedule - View")) {
      throw redirect({ to: "/forbidden" });
    }
  },
  component: DeliverySchedulingShow,
});
